import { isLeft, isRight } from 'fp-ts/Either';
import { use, useState } from 'react';
import { TRPCContext } from '../contexts/TRPC';
import { captureAndReportError } from '../utils/errorReporting';
import { usePolling } from './usePolling';

export interface ExternalSyncStatus {
  providers: Array<{
    provider: string;
    lastSyncTimestamp: Date | null;
    lastSyncStatus: string | null;
    errorMessage: string | null;
  }>;
  overallHealth: 'healthy' | 'warning' | 'error' | 'unknown';
  lastSuccessfulSync: Date | null;
}

interface UseExternalSyncStatusOptions {
  componentName: string;
  refreshInterval?: number;
  enabled?: boolean;
}

export function useExternalSyncStatus({
  componentName,
  refreshInterval = 30000,
  enabled = true,
}: UseExternalSyncStatusOptions) {
  const trpcContext = use(TRPCContext);
  const [syncStatus, setSyncStatus] = useState<ExternalSyncStatus | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const loadSyncStatus = async () => {
    if (!enabled || !trpcContext?.client) return;

    try {
      setIsLoading(true);
      const result =
        await trpcContext.client.getExternalSyncStatus.query(undefined);

      if (isRight(result)) {
        const convertedStatus: ExternalSyncStatus = {
          ...result.right,
          providers: result.right.providers.map((provider) => ({
            ...provider,
            lastSyncTimestamp: provider.lastSyncTimestamp
              ? new Date(provider.lastSyncTimestamp)
              : null,
          })),
          lastSuccessfulSync: result.right.lastSuccessfulSync
            ? new Date(result.right.lastSuccessfulSync)
            : null,
        };
        setSyncStatus(convertedStatus);
      } else if (isLeft(result)) {
        captureAndReportError(new Error(result.left.message), {
          operation: 'admin.loadSyncStatus',
          component: componentName,
          errorName: result.left.name,
        });
      }
    } catch (error) {
      captureAndReportError(error, {
        operation: 'admin.loadSyncStatus',
        component: componentName,
      });
    } finally {
      setIsLoading(false);
    }
  };

  usePolling(loadSyncStatus, refreshInterval, {
    enabled,
    immediate: true,
  });

  return { syncStatus, isLoading, refresh: loadSyncStatus };
}
